import {
  type AccountInfo,
  type Configuration,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type SilentRequest,
} from "@azure/msal-browser";

/**
 * MSAL configuration sourced from VITE_PTRN_* env vars.
 *
 * The SPA app reg (`ptrn-super-web`) is configured with both
 * `https://ptrn.frontierfirm.app/` and `http://localhost:5173/` as
 * redirect URIs, so the same app reg works for prod + local dev.
 *
 * Storage choice: sessionStorage. The token survives a tab refresh but
 * not a tab close. Trades a little re-login friction for a smaller blast
 * radius on shared machines.
 */
const REDIRECT_URI =
  typeof window !== "undefined" ? window.location.origin + "/" : "/";

const MSAL_CONFIG: Configuration = {
  auth: {
    authority: import.meta.env.VITE_PTRN_AUTHORITY,
    clientId: import.meta.env.VITE_PTRN_CLIENT_ID,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

export const API_SCOPE = import.meta.env.VITE_PTRN_API_SCOPE;

export const msalInstance = new PublicClientApplication(MSAL_CONFIG);

/**
 * Initialise MSAL. Must be awaited once at app boot before rendering
 * anything that depends on accounts. Internally MSAL uses this to
 * complete any in-flight redirect handshake and rehydrate caches.
 */
export async function initMsal(): Promise<void> {
  await msalInstance.initialize();
  // Surface the redirect promise so any `loginRedirect` round-trip
  // completes before the React tree renders.
  const result = await msalInstance.handleRedirectPromise();
  if (result?.account) {
    msalInstance.setActiveAccount(result.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }
}

/**
 * Acquire an access token for the PTRN API. Tries silent acquisition
 * first; falls back to redirect on consent / interaction required.
 *
 * Throws if no account is signed in - callers should gate UI behind
 * `useIsAuthenticated()` before invoking.
 */
export async function acquireApiToken(account?: AccountInfo): Promise<string> {
  const target =
    account ??
    msalInstance.getActiveAccount() ??
    msalInstance.getAllAccounts()[0];
  if (!target) {
    throw new Error("No signed-in account; call signIn() first.");
  }

  const request: SilentRequest = {
    account: target,
    scopes: [API_SCOPE],
  };

  try {
    const result = await msalInstance.acquireTokenSilent(request);
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Redirect flow blocks the page; the response lands in
      // initMsal()'s handleRedirectPromise on the next load.
      await msalInstance.acquireTokenRedirect({
        scopes: [API_SCOPE],
      });
      // The line above never returns, but TS needs a return.
      throw new Error("Redirecting for interactive auth");
    }
    throw err;
  }
}

/** Trigger interactive sign-in. Page redirects out. */
export async function signIn(): Promise<void> {
  await msalInstance.loginRedirect({
    scopes: [API_SCOPE],
  });
}

/** Sign out and clear local state. Page redirects out. */
export async function signOut(): Promise<void> {
  const account = msalInstance.getActiveAccount();
  await msalInstance.logoutRedirect({
    account: account ?? undefined,
  });
}
